import { motion, useScroll } from "framer-motion";
import { useRef } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12,
    },
  },
  hover: {
    y: -8,
    scale: 1.02,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 17,
    },
  },
};

export default function SpecializedTuningServices() {
  const sectionRef = useRef(null);

  useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  return (
    <section ref={sectionRef} className="bg-[#1A1A1A] text-white py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">

        {/* Heading */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold relative inline-block"
          >
            Restoration Services
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="absolute -bottom-2 left-0 w-full h-0.5 bg-gradient-to-r from-amber-500 to-transparent"
            />
          </motion.h2>

          <motion.p
            variants={fadeInUp}
            className="text-gray-400 mt-3 max-w-2xl mx-auto text-sm"
          >
            Every car and owner has a story and mission to fulfill.
          </motion.p>
        </motion.div>

        {/* Cards */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 mb-20 items-stretch"
        >
          {[
            {
              title: "Interiors & Décor",
              img: "/assets/perfomance/int.jpg",
              gradient: "from-amber-600 to-amber-400",
              bgHover: "radial-gradient(ellipse at top left, rgba(180,120,20,0.14) 0%, rgba(120,80,10,0.07) 55%, transparent 80%)",
              desc: "Enhance comfort and aesthetics with premium interior restoration and custom detailing.",
            },
            {
              title: "Dismantling & Structure",
              img: "/assets/perfomance/img4.jpg",
              gradient: "from-amber-600 to-amber-400",
              bgHover: "radial-gradient(ellipse at top left, rgba(180,120,20,0.14) 0%, rgba(120,80,10,0.07) 55%, transparent 80%)",
              desc: "Careful dismantling ensures structural integrity and safe restoration.",
            },
            {
              title: "Frame and Body",
              img: "/assets/perfomance/frame.jpg",
              gradient: "from-amber-600 to-amber-400",
              bgHover: "radial-gradient(ellipse at top left, rgba(180,120,20,0.14) 0%, rgba(120,80,10,0.07) 55%, transparent 80%)",
              desc: "Structural repair and refinishing for durability and visual appeal.",
            },
            {
              title: "Engine & Transmission",
              img: "/assets/perfomance/eng.jpg",
              gradient: "from-amber-600 to-amber-400",
              bgHover: "radial-gradient(ellipse at top left, rgba(180,120,20,0.14) 0%, rgba(120,80,10,0.07) 55%, transparent 80%)",
              desc: "Precision tuning and rebuilding for peak performance.",
            },
          ].map((card, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover="hover"
              className="group relative h-full"
            >
              {/* Glow */}
              <motion.div
                className={`absolute -inset-0.5 bg-gradient-to-r ${card.gradient} rounded-lg opacity-0 blur-lg group-hover:opacity-30`}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />

              <motion.div
                className="relative border border-gray-700 rounded-lg overflow-hidden h-full flex flex-col"
                style={{ backgroundColor: "#151515" }}
                whileHover={{ backgroundColor: "#181408" }}
                transition={{ duration: 0.5 }}
              >
                {/* Animated radial bg on hover */}
                <motion.div
                  className="absolute inset-0 pointer-events-none z-0"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  style={{ background: card.bgHover }}
                />

                {/* Image */}
                <motion.div
                  className="overflow-hidden relative z-10"
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src={card.img}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                    alt={card.title}
                  />
                </motion.div>

                {/* Overlay */}
                <motion.div className="absolute inset-0 bg-gradient-to-t from-amber-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />

                {/* Content */}
                <div className="p-5 relative flex flex-col flex-grow z-10">
                  <motion.h3
                    className="font-semibold text-lg mb-2"
                    whileHover={{ x: 5, color: "#f59e0b" }}
                    transition={{ duration: 0.25 }}
                  >
                    {card.title}
                  </motion.h3>

                  <p className="text-sm text-gray-400 mt-auto">
                    {card.desc}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  );
}